import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import {
  actionsExecuting,
  ActionsExecuting,
} from '@ngxs-labs/actions-executing';
import { Store } from '@ngxs/store';
import { debounceTime, filter, map, Observable, tap } from 'rxjs';
import { LoadingComponent } from './core/layout/loading/loading.component';
import { NotificationsService } from './core/notifications/services/notifications.service';
import { FooterComponent } from './layout/footer/footer.component';
import { HeaderComponent } from './layout/header/header.component';
import {
  LoadCategoriesList,
  LoadingActions,
  ResetGalleryFilters,
} from './store';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeaderComponent,
    RouterOutlet,
    FooterComponent,
    AsyncPipe,
    LoadingComponent,
  ],
})
export class AppComponent implements OnInit {
  constructor(
    private router: Router,
    private store: Store,
    private destroyRef: DestroyRef,
    private notificationsService: NotificationsService,
  ) {}

  @ViewChild('notificationsContainer', { read: ViewContainerRef, static: true })
  set notificationsContainer(container: ViewContainerRef) {
    if (container) {
      this.notificationsService.notificationContainer = container;
    }
  }

  protected loadingActions$: Observable<ActionsExecuting> = inject(
    Store,
  ).select(actionsExecuting(LoadingActions));

  protected isLoading$!: Observable<boolean>;

  protected isLeavingGallery: boolean = false;

  ngOnInit(): void {
    this.isLoading$ = this.loadingActions$.pipe(
      debounceTime(1000),
      map((actionsExecuting: ActionsExecuting) => {
        return !!actionsExecuting;
      }),
    );
    this.handleLeaveGallery();
    this.store.dispatch(new LoadCategoriesList());
  }

  private handleLeaveGallery(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationStart),
        filter((event: NavigationStart) => this.isGalleryRoute(event.url)),
        tap(() => {
          this.isLeavingGallery = true;
        }),
      )
      .subscribe();

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd),
        tap(() => {
          if (this.isLeavingGallery) {
            this.isLeavingGallery = false;
            this.resetGalleryFilters();
          }
        }),
      )
      .subscribe();
  }

  private resetGalleryFilters(): void {
    this.store.dispatch(new ResetGalleryFilters());
  }

  private isGalleryRoute(url: string): boolean {
    const galleryPattern = /^\/gallery(\?.*)?$/; // Matches /gallery or /gallery?query=params
    return galleryPattern.test(this.router.url) && !galleryPattern.test(url);
  }
}
